/**
 * Portal Role - represents a role assigned to a portal user
 */
export interface PortalRole {
  portalRoleId: number;
  portalId: number;
  name: string;
  friendlyName?: string;
  description?: string | null;
  active: boolean;
}

/**
 * Client Permission - represents permissions for a specific client
 */
export interface ClientPermission {
  mCode: string;
  cCodes: string[];
}

/**
 * Portal User - represents a user in the portal system
 */
export interface PortalUser {
  identityProviderUserId: string;
  portalUserId: string;
  name: string;
  email: string;
  active: boolean;
  /** Company-scoped list only. False when every permission row for this company is inactive. */
  activeInCompany?: boolean;
  roles: PortalRole[];
  clientPermissions: ClientPermission[];
  portals: any[];
  rolesByPortal: Record<string, any>;
  allPermissionCodes: string[];
}

/**
 * API Message - represents a message from the API response
 */
export interface ApiMessage {
  errorType: string;
  message: string;
}

/**
 * Portal User Response - new API response structure
 */
export interface PortalUserResponse {
  valid: boolean;
  messages: ApiMessage[];
  data: PortalUser[];
}

/**
 * Portal User List Response - paginated response from the API (deprecated)
 * @deprecated Use PortalUserResponse instead
 */
export interface PortalUserListResponse {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  hasPrevious: boolean;
  hasNext: boolean;
  items: PortalUser[];
}

/**
 * Error DTO - represents an error message from the API
 */
export interface ErrorDto {
  errorType: string;
  message: string;
}

/**
 * Base Response DTO - generic API response structure
 */
export interface BaseResponseDto<T> {
  valid: boolean;
  messages: ErrorDto[];
  data?: T;
}

/**
 * Client Permission DTO - represents a client permission for creating/updating users
 */
export interface ClientPermissionDto {
  mCode: string;
  cCode?: string | null; // null or empty string = access to all CCodes for this MCode
}

/**
 * Upsert Portal User DTO - data transfer object for creating or updating portal users
 */
export interface UpsertPortalUserDto {
  email: string;
  name: string;
  active: boolean;
  roleIds: number[];
  clientPermissions: ClientPermissionDto[];
}

/**
 * Create User Status - indicates whether a new user was created or an existing user was assigned
 */
export enum CreateUserStatus {
  CreatedNewUser = 'CreatedNewUser',
  AssignedExistingUser = 'AssignedExistingUser'
}

/**
 * Create User Response DTO - response from creating/assigning a user
 */
export interface CreateUserResponseDto {
  user: PortalUser;
  status: CreateUserStatus;
}

/**
 * Assignable Role DTO - represents a role that can be assigned to external users
 */
export interface AssignableRoleDto {
  roleId: number;
  name: string;
  friendlyName: string;
  description?: string | null;
}
